import { apiClient } from '@/lib/api/client'
import type {
  Media,
  MediaListResponse,
  MediaDateFolder,
  CreateMediaData,
  UpdateMediaData,
  BulkDeleteMediaData,
  MediaListParams,
} from '@/types/media'

export const MediaService = {
  /**
   * Get list of media items
   */
  async getMedia(params?: MediaListParams): Promise<MediaListResponse> {
    try {
      const response = await apiClient.get('/content-management/media-library', { params })
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch media')
    }
  },

  /**
   * Get media by ID
   */
  async getMediaItem(id: number): Promise<Media> {
    if (!id || id <= 0) {
      throw new Error('Invalid media ID')
    }
    try {
      const response = await apiClient.get(`/content-management/media-library/${id}`)
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch media item')
    }
  },

  /**
   * Upload media files
   */
  async uploadMedia(
    data: CreateMediaData,
    onProgress?: (progress: number, fileIndex: number) => void
  ): Promise<Media> {
    try {
      const formData = new FormData()
      data.files.forEach((file) => {
        formData.append('files[]', file)
      })

      const response = await apiClient.post('/content-management/media-library', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            // For multiple files, we'll track overall progress
            // Individual file progress would require separate requests per file
            onProgress(percentCompleted, 0)
          }
        },
      })
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to upload media')
    }
  },

  /**
   * Upload a single media file with progress tracking
   */
  async uploadMediaFile(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<Media> {
    try {
      const formData = new FormData()
      formData.append('files[]', file)

      const response = await apiClient.post('/content-management/media-library', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            onProgress(percentCompleted)
          }
        },
      })
      // API returns an array of uploaded media, get the first one
      const uploadedMedia = Array.isArray(response.data) ? response.data[0] : response.data
      return uploadedMedia
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to upload media')
    }
  },

  /**
   * Update media item
   */
  async updateMedia(id: number, data: UpdateMediaData): Promise<Media> {
    if (!id || id <= 0) {
      throw new Error('Invalid media ID')
    }
    try {
      const response = await apiClient.put(`/content-management/media-library/${id}`, data)
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update media')
    }
  },

  /**
   * Delete media item
   */
  async deleteMedia(id: number): Promise<void> {
    if (!id || id <= 0) {
      throw new Error('Invalid media ID')
    }
    try {
      await apiClient.delete(`/content-management/media-library/${id}`)
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete media')
    }
  },

  /**
   * Bulk delete media items
   */
  async bulkDeleteMedia(data: BulkDeleteMediaData): Promise<void> {
    if (!data.ids || data.ids.length === 0) {
      throw new Error('No media items selected')
    }
    try {
      await apiClient.post('/content-management/media-library/bulk/delete', data)
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete media items')
    }
  },

  /**
   * Get date folders for filtering
   */
  async getDateFolders(): Promise<MediaDateFolder[]> {
    try {
      const response = await apiClient.get('/options/dates')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch date folders')
    }
  },
}

